@if (loading) {
  <div class="rounded-2xl border border-gray-200 bg-white p-5 text-center dark:border-gray-800 dark:bg-white/[0.03] md:p-8">
    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Loading Suppliers</h3>
    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
      Pulling the latest supplier details for this event.
    </p>
  </div>
} @else if (loadError) {
  <div class="rounded-2xl border border-red-200 bg-red-50 p-5 text-center dark:border-red-900/50 dark:bg-red-950/20 md:p-8">
    <h3 class="text-lg font-semibold text-red-700 dark:text-red-300">Suppliers unavailable</h3>
    <p class="mt-2 text-sm text-red-600 dark:text-red-200">{{ loadError }}</p>
    <div class="mt-4 flex justify-center">
      <app-button
        [label]="'Retry'"
        [style]="'secondary'"
        (click)="loadSuppliers()">
      </app-button>
    </div>
  </div>
} @else {
  @if (saving) {
    <div class="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/25 backdrop-blur-sm">
      <div class="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-lg dark:border-gray-800 dark:bg-gray-900">
        <span class="loading-spinner"></span>
        <div class="flex flex-col">
          <span class="text-sm font-semibold text-gray-900 dark:text-white">Saving suppliers</span>
          <span class="text-xs text-gray-600 dark:text-gray-400">Please wait while changes and uploads are processed…</span>
        </div>
      </div>
    </div>
  }

  <div class="space-y-4" [class.pointer-events-none]="saving">
    @if (!hasSuppliers()) {
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-8 text-center shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] md:px-6 md:py-10">
        <h4 class="text-lg font-semibold text-gray-900 dark:text-white">No suppliers added yet.</h4>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Use the button below to add a supplier to this event.
        </p>
        <div class="mt-6">
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-brand-300 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 transition hover:bg-brand-100 dark:border-brand-800 dark:bg-brand-950/20 dark:text-brand-300 dark:hover:bg-brand-950/30"
            (click)="addSupplier()">
            <lucide-icon [name]="PlusIcon" [size]="14"></lucide-icon>
            Add Supplier
          </button>
        </div>
      </div>
    } @else {
      <div class="space-y-4">
        @for (supplierGroup of supplierGroups.controls; track supplierGroup; let supplierIndex = $index) {
          <div
            [id]="supplierAnchorId(supplierIndex)"
            class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
            <div class="flex items-center justify-between border-b border-gray-200 pb-3 dark:border-gray-800">
              <h4 class="text-sm font-semibold uppercase tracking-[0.08em] text-brand-700 dark:text-brand-300">{{ supplierCardTitle(supplierIndex) }}</h4>
              <button
                type="button"
                class="text-sm font-medium text-red-600 transition hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
                (click)="removeSupplier(supplierIndex)">
                Remove
              </button>
            </div>

            <div class="space-y-0 pt-4">
              <div class="grid gap-3 border-b border-gray-100 py-3 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <div class="pt-3">Type of Supplier</div>
                </div>
                <div>
                  <app-text-input
                    [control]="supplierTypeControl(supplierIndex)"
                    [placeholder]="'e.g. Florist, Band, Photographer'">
                  </app-text-input>
                </div>
              </div>

              <div class="grid gap-3 border-b border-gray-100 py-3 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <div class="pt-3">Company Name</div>
                </div>
                <div>
                  <app-text-input [control]="companyNameControl(supplierIndex)"></app-text-input>
                </div>
              </div>

              <div class="grid gap-3 border-b border-gray-100 py-3 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <div class="pt-3">Contact Name</div>
                </div>
                <div>
                  <app-text-input [control]="contactNameControl(supplierIndex)"></app-text-input>
                </div>
              </div>

              <div class="grid gap-3 border-b border-gray-100 py-3 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <div class="pt-3">Telephone</div>
                </div>
                <div>
                  <app-text-input [control]="telephoneControl(supplierIndex)"></app-text-input>
                </div>
              </div>

              <div class="grid gap-3 border-b border-gray-100 py-3 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <div class="pt-3">Email Address</div>
                </div>
                <div>
                  <app-text-input [control]="emailAddressControl(supplierIndex)" [type]="'email'"></app-text-input>
                </div>
              </div>

              <div class="grid gap-3 border-b border-gray-100 py-3 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <div class="pt-3">Arrival Time</div>
                </div>
                <div class="max-w-xs">
                  <app-time-picker [control]="arrivalTimeControl(supplierIndex)"></app-time-picker>
                </div>
              </div>

              <div class="grid gap-3 border-b border-gray-100 py-3 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <div class="pt-3">Public Liability Insurance</div>
                </div>
                <div class="space-y-3">
                  <div class="flex flex-col gap-3 xl:flex-row xl:items-center">
                    <div class="w-full xl:max-w-[11rem]">
                      <app-select
                        [control]="pliStatusControl(supplierIndex)"
                        [choices]="statusChoices"
                        [placeholder]="'— Select —'">
                      </app-select>
                    </div>

                    <div class="flex flex-wrap items-center gap-2">
                      <input
                        #pliInput
                        type="file"
                        class="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        (change)="onAttachmentSelected($event, supplierIndex, 'pli')">
                      <button
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-brand-200 bg-brand-50 px-3 py-2 text-xs font-medium text-brand-700 transition hover:bg-brand-100 dark:border-brand-900/40 dark:bg-brand-950/20 dark:text-brand-300 dark:hover:bg-brand-950/30"
                        (click)="pliInput.click()">
                        <lucide-icon [name]="FileUpIcon" [size]="14"></lucide-icon>
                        Upload
                      </button>

                      <span
                        class="text-xs"
                        [ngClass]="hasAttachment(supplierIndex, 'pli') ? 'font-medium text-green-600 dark:text-green-300' : 'text-gray-500 dark:text-gray-400'">
                        {{ attachmentName(supplierIndex, 'pli') }}
                      </span>

                      @if (attachmentUrl(supplierIndex, 'pli')) {
                        <a
                          [href]="attachmentUrl(supplierIndex, 'pli')!"
                          target="_blank"
                          rel="noreferrer"
                          class="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200">
                          View
                        </a>
                      }

                      @if (hasAttachment(supplierIndex, 'pli')) {
                        <button
                          type="button"
                          class="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
                          (click)="clearAttachment(supplierIndex, 'pli')">
                          <lucide-icon [name]="TrashIcon" [size]="12"></lucide-icon>
                          Remove
                        </button>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div class="grid gap-3 border-b border-gray-100 py-3 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <div class="pt-3">PAT Testing Certificate (if applicable)</div>
                </div>
                <div class="space-y-3">
                  <div class="flex flex-col gap-3 xl:flex-row xl:items-center">
                    <div class="w-full xl:max-w-[11rem]">
                      <app-select
                        [control]="patStatusControl(supplierIndex)"
                        [choices]="statusChoices"
                        [placeholder]="'— Select —'">
                      </app-select>
                    </div>

                    <div class="flex flex-wrap items-center gap-2">
                      <input
                        #patInput
                        type="file"
                        class="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        (change)="onAttachmentSelected($event, supplierIndex, 'pat')">
                      <button
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-brand-200 bg-brand-50 px-3 py-2 text-xs font-medium text-brand-700 transition hover:bg-brand-100 dark:border-brand-900/40 dark:bg-brand-950/20 dark:text-brand-300 dark:hover:bg-brand-950/30"
                        (click)="patInput.click()">
                        <lucide-icon [name]="FileUpIcon" [size]="14"></lucide-icon>
                        Upload
                      </button>

                      <span
                        class="text-xs"
                        [ngClass]="hasAttachment(supplierIndex, 'pat') ? 'font-medium text-green-600 dark:text-green-300' : 'text-gray-500 dark:text-gray-400'">
                        {{ attachmentName(supplierIndex, 'pat') }}
                      </span>

                      @if (attachmentUrl(supplierIndex, 'pat')) {
                        <a
                          [href]="attachmentUrl(supplierIndex, 'pat')!"
                          target="_blank"
                          rel="noreferrer"
                          class="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200">
                          View
                        </a>
                      }

                      @if (hasAttachment(supplierIndex, 'pat')) {
                        <button
                          type="button"
                          class="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
                          (click)="clearAttachment(supplierIndex, 'pat')">
                          <lucide-icon [name]="TrashIcon" [size]="12"></lucide-icon>
                          Remove
                        </button>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 border-t border-gray-100 pt-4 dark:border-gray-800">
              <div class="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">Notes</div>
              <app-textarea
                [control]="notesControl(supplierIndex)"
                [placeholder]="'Any notes specific to this supplier...'"
                [rows]="4">
              </app-textarea>
            </div>
          </div>
        }

        <!-- TVG-266: compact, left-aligned dashed "add" button matching the
             portal's existing add-row pattern (e.g. planner "Add another
             contact"). Kept deliberately distinct from the solid Save button so
             it no longer reads as the primary save action. -->
        <div>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-brand-300 bg-brand-50 px-3 py-2 text-xs font-medium text-brand-700 transition hover:bg-brand-100 dark:border-brand-800 dark:bg-brand-950/20 dark:text-brand-300 dark:hover:bg-brand-950/30"
            (click)="addSupplier()">
            <lucide-icon [name]="PlusIcon" [size]="14"></lucide-icon>
            Add Supplier
          </button>
        </div>
      </div>
    }

    <div
      [id]="supplierNotesAnchorId()"
      class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
      <div class="border-b border-gray-200 pb-3 dark:border-gray-800">
        <h4 class="text-sm font-semibold uppercase tracking-[0.08em] text-brand-700 dark:text-brand-300">Supplier Notes</h4>
      </div>
      <div class="pt-4">
        <app-textarea
          [control]="supplierNotesControl"
          [placeholder]="'Any general notes regarding suppliers for this event...'"
          [rows]="5">
        </app-textarea>
      </div>
    </div>

    <div class="flex flex-wrap justify-end gap-3 border-t border-gray-200 pt-5 dark:border-gray-800">
      <app-button
        [label]="'Cancel'"
        [style]="'secondary'"
        [disabled]="saving || !hasUnsavedChanges()"
        (click)="cancelChanges()">
      </app-button>
      <app-button
        [label]="saving ? 'Saving…' : 'Save Changes'"
        [disabled]="saving || !hasUnsavedChanges()"
        (click)="saveChanges()">
      </app-button>
    </div>
  </div>
}
